import { FC } from 'react';
import { ActionsMenu, Modal, Table } from 'melp-design/components';
import { useTranslation } from 'react-i18next';
import { ModalProps, useModalContext } from 'store/modal';
import {
  useCustomCategoryValues,
  useDeleteCustomCategoryValue,
} from 'store/custom-categories';
import { usePredefinedToasts } from 'utils/Toast';
import { useConfirm } from 'store/confirm';
import { EditCustomCategoryValueModal } from 'containers/custom-categories';

interface Props extends ModalProps {
  categoryId: string;
  categoryName: string;
}

export const CustomCategoryValuesModal: FC<Props> = ({
  closeModal,
  categoryId,
  categoryName,
}) => {
  const { t } = useTranslation();
  const predefinedToasts = usePredefinedToasts();
  const { confirmation } = useConfirm();
  const { openModal } = useModalContext();

  const { data: values, isLoading } = useCustomCategoryValues(categoryId);

  const { mutate: deleteValue, isLoading: isDeleting } =
    useDeleteCustomCategoryValue();

  return (
    <Modal.Content
      title={categoryName}
      actions={[
        {
          variant: 'neutral-outline',
          onClick: () => closeModal(),
          label: t('common.close'),
        },
      ]}
    >
      <Table
        data={values?.items ?? []}
        isLoading={isLoading}
        columns={[
          {
            id: 'name',
            header: t('entity_type.employee'),
            cell: ({ row: { original: value } }) => value.name,
          },
          {
            id: 'employeeCount',
            header: t('employees.employee_count'),
            cell: ({ row: { original: value } }) => value.employeeCount,
            meta: { align: 'right' },
          },
          {
            id: 'actions',
            cell: ({ row: { original: value } }) => {
              return (
                <ActionsMenu
                  type="compact"
                  items={[
                    {
                      label: t('employees.copyCategoryValue'),
                      onClick: () => {
                        navigator.clipboard.writeText(value.name);
                      },
                    },
                    {
                      label: t('common.edit'),
                      onClick: () => {
                        openModal({
                          component: EditCustomCategoryValueModal,
                          props: {
                            categoryId,
                            valueId: value.id,
                            initialValues: {
                              name: value.name,
                            },
                          },
                        });
                      },
                    },
                    {
                      label: t('common.remove'),
                      onClick: async () => {
                        const { confirmed } = await confirmation(
                          t('common.areYouSureToDelete', {
                            title: value.name,
                          }),
                        );

                        if (confirmed) {
                          deleteValue(
                            { categoryId, valueId: value.id },
                            {
                              onSuccess: () => {
                                predefinedToasts.success.deleted();
                              },
                            },
                          );
                        }
                      },
                      color: 'error',
                      disabled: isDeleting,
                    },
                  ]}
                />
              );
            },
            meta: { align: 'right' },
          },
        ]}
      />
    </Modal.Content>
  );
};
